import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { DataQualityByClientResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { seriesPrimary, chartChrome } from '../../utils/chartColors';
import './DataQualityByClientChart.scss';

interface DataQualityByClientChartProps {
  data: DataQualityByClientResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (order: 'asc' | 'desc') => void;
}

const DataQualityByClientChart: FC<DataQualityByClientChartProps> = ({
  data,
  isLoading,
  error,
  onRetry,
  sortOrder = 'asc',
  onSortOrderChange,
}) => {
  const { t } = useTranslation();
  /**
   * The chart draws ten clients, and the sort toggle decides which ten: the
   * worst or the best. A static title would misdescribe one of those states.
   */
  const title = String(
    t(
      sortOrder === 'desc'
        ? 'cpi.analytics.dataQualityByClientHighest'
        : 'cpi.analytics.dataQualityByClientLowest'
    )
  );
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const clients = data.data.map((d) =>
      d.clientId.length > 12 ? `${d.clientId.substring(0, 12)}…` : d.clientId
    );
    const values = data.data.map((d) => d.dataQuality);
    const chrome = chartChrome();

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        // Bar value is the % of trips with a valid CPI; the tooltip surfaces how many
        // trips have no CPI and why (zero weight / zero distance / no address).
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const d = data.data[p?.dataIndex ?? -1];
          if (!d) return '';
          const lines = [
            `<strong>${d.clientId}</strong>`,
            `${d.dataQuality}% ${t('cpi.analytics.dataQualityWithCpi')}`,
            `${t('cpi.analytics.dataQualityWithoutCpiTrips', { count: d.tripsWithoutCpi })}`,
          ];
          const reasonParts: string[] = [];
          if (d.reasons?.zeroWeight) reasonParts.push(`${t('cpi.ingestion.errorCategory.zero_weight')}: ${d.reasons.zeroWeight}`);
          if (d.reasons?.zeroDistance) reasonParts.push(`${t('cpi.analytics.dataQualityZeroDistance')}: ${d.reasons.zeroDistance}`);
          if (d.reasons?.noAddress) reasonParts.push(`${t('cpi.ingestion.errorCategory.no_address')}: ${d.reasons.noAddress}`);
          if (d.reasons?.notCalculatedYet) reasonParts.push(`${t('cpi.analytics.dataQualityNotCalculated')}: ${d.reasons.notCalculatedYet}`);
          if (reasonParts.length > 0) lines.push(`<span style="opacity:0.8">${reasonParts.join(' · ')}</span>`);
          return lines.join('<br/>');
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: clients,
        axisLabel: { rotate: 30, fontSize: 11, color: chrome.muted },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.dataQualityPct')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        min: 0,
        max: 100,
        axisLabel: {
          color: chrome.muted,
          formatter: '{value}%',
        },
      },
      series: [
        {
          type: 'bar',
          data: values,
          itemStyle: { color: seriesPrimary() },
          barMaxWidth: 40,
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            formatter: (params: any) => `${params.value}%`,
          },
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, t]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  const header = (
    <div className="data-quality-by-client-chart__header">
      <h3 className="data-quality-by-client-chart__title">{title}</h3>
      {onSortOrderChange && (
        <div className="data-quality-by-client-chart__sort-toggle">
          <button
            className={`data-quality-by-client-chart__sort-btn${sortOrder === 'asc' ? ' data-quality-by-client-chart__sort-btn--active' : ''}`}
            type="button"
            onClick={() => onSortOrderChange('asc')}
          >
            ↑ ASC
          </button>
          <button
            className={`data-quality-by-client-chart__sort-btn${sortOrder === 'desc' ? ' data-quality-by-client-chart__sort-btn--active' : ''}`}
            type="button"
            onClick={() => onSortOrderChange('desc')}
          >
            ↓ DESC
          </button>
        </div>
      )}
    </div>
  );

  if (error && !isLoading) {
    return (
      <div className="data-quality-by-client-chart">
        {header}
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="data-quality-by-client-chart data-quality-by-client-chart--loading">
        {header}
        <div className="data-quality-by-client-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="data-quality-by-client-chart">
        {header}
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="data-quality-by-client-chart">
      {header}
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default DataQualityByClientChart;
